/**
 * Shared utility functions for data sanitization and privacy
 */

/**
 * Sanitize string input to prevent XSS and injection attacks
 * Removes special characters and limits length
 */
export function sanitizeInput(value: string | null | undefined, maxLength = 500): string {
  if (!value) return '';

  return value
    .trim()
    // Remove HTML tags and script content
    .replace(/<[^>]*>/g, '')
    // Remove potentially dangerous characters but keep common ones
    .replace(/[^\w\s\-_.@#]/g, '')
    // Limit length
    .substring(0, maxLength);
}

/**
 * Sanitize Stripe metadata value
 * Stripe has specific requirements: max 500 chars, specific allowed characters
 */
export function sanitizeMetadata(value: string | null | undefined): string {
  if (!value) return '';

  return value
    .trim()
    // Stripe allows: letters, numbers, spaces, and -_.@
    .replace(/[^\w\s\-_.@]/g, '')
    // Stripe limit is 500 characters
    .substring(0, 500);
}

/**
 * Generate a unique event ID for deduplication
 * Falls back to timestamp-based ID if crypto.randomUUID is not available
 */
export function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers or non-HTTPS contexts
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Create a unique tracking key to prevent collisions
 */
export function createTrackingKey(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

